export const FULLSTORY_PAGES = Object.freeze({
  marketing: Object.freeze({
    landing: "Landing",
    pricing: "Pricing",
    showRegister: "Show Register",
    forAdministrators: "For Administrators",
    blog: "Blog",
    toolsHub: "Tools Hub",
    classroomTool: "Classroom Tool",
    whatsNew: "Whats New",
    referralLanding: "Referral Landing",
    privacy: "Privacy",
    trustCenter: "Trust Center",
    classroomAutomation: "Classroom Automation",
    comparison: "Comparison",
    demoScenario: "Demo Scenario",
    ambassadors: "Ambassador Program",
  }),
  authentication: Object.freeze({
    login: "Login",
    register: "Register",
  }),
  billing: Object.freeze({
    billing: "Billing",
    checkoutHelp: "Pricing Checkout Help",
  }),
  product: Object.freeze({
    teacherHub: "Teacher Hub",
    classManager: "Class Manager",
    classEditor: "Class Editor",
    contentManager: "Content Manager",
    presentCalendar: "Present Calendar",
    present: "Present",
    scheduleManager: "Schedule Manager",
    automationManager: "Automation Manager",
    automationBuilder: "Automation Builder",
    automationTimeline: "Automation Timeline",
    eventsManager: "Events Manager",
    settings: "Settings",
    testimonial: "Testimonial",
    helpCenter: "Help Center",
    liveOfficeHoursJoin: "Live Office Hours Join",
    referralProgram: "Referral Program",
    soundMeterHistory: "Sound Meter History",
    autoStatus: "Automation Status",
    printExport: "Print Export",
  }),
  system: Object.freeze({
    reactivationGate: "Reactivation Gate",
  }),
});

const STATIC_PAGE_ROUTES = [
  ["/pricing/checkout-help", FULLSTORY_PAGES.billing.checkoutHelp],
  ["/account/subscription", FULLSTORY_PAGES.billing.billing],
  ["/auth/register", FULLSTORY_PAGES.authentication.register],
  ["/auth/login", FULLSTORY_PAGES.authentication.login],
  ["/show-pricing", FULLSTORY_PAGES.marketing.pricing],
  ["/pricing", FULLSTORY_PAGES.marketing.pricing],
  ["/show", FULLSTORY_PAGES.marketing.showRegister],
  ["/for-administrators", FULLSTORY_PAGES.marketing.forAdministrators],
  ["/ambassadors", FULLSTORY_PAGES.marketing.ambassadors],
  ["/why-lesson-launchpad", FULLSTORY_PAGES.marketing.landing],
  ["/what-is-classroom-automation", FULLSTORY_PAGES.marketing.classroomAutomation],
  ["/tools", FULLSTORY_PAGES.marketing.toolsHub],
  ["/classroom-timer", FULLSTORY_PAGES.marketing.classroomTool],
  ["/countdown-timer", FULLSTORY_PAGES.marketing.classroomTool],
  ["/random-student-picker", FULLSTORY_PAGES.marketing.classroomTool],
  ["/classroomscreen-alternative", FULLSTORY_PAGES.marketing.classroomTool],
  ["/metronome", FULLSTORY_PAGES.marketing.classroomTool],
  ["/tone-generator", FULLSTORY_PAGES.marketing.classroomTool],
  ["/out-of-tune", FULLSTORY_PAGES.marketing.classroomTool],
  ["/music-tools", FULLSTORY_PAGES.marketing.classroomTool],
  ["/tuner", FULLSTORY_PAGES.marketing.classroomTool],
  ["/whats-new", FULLSTORY_PAGES.marketing.whatsNew],
  ["/privacy", FULLSTORY_PAGES.marketing.privacy],
  ["/trust", FULLSTORY_PAGES.marketing.trustCenter],
  ["/teacher-hub", FULLSTORY_PAGES.product.teacherHub],
  ["/classes-manager", FULLSTORY_PAGES.product.classManager],
  ["/form", FULLSTORY_PAGES.product.classEditor],
  ["/schedule-manager", FULLSTORY_PAGES.product.scheduleManager],
  ["/automations-manager", FULLSTORY_PAGES.product.automationManager],
  ["/automated-routine-builder", FULLSTORY_PAGES.product.automationBuilder],
  ["/automation-timeline", FULLSTORY_PAGES.product.automationTimeline],
  ["/events-manager", FULLSTORY_PAGES.product.eventsManager],
  ["/settings/testimonial", FULLSTORY_PAGES.product.testimonial],
  ["/settings", FULLSTORY_PAGES.product.settings],
  ["/faq", FULLSTORY_PAGES.product.helpCenter],
  ["/live-office-hours/join", FULLSTORY_PAGES.product.liveOfficeHoursJoin],
  ["/referral-program", FULLSTORY_PAGES.product.referralProgram],
  ["/sound-meter-history", FULLSTORY_PAGES.product.soundMeterHistory],
  ["/auto-status", FULLSTORY_PAGES.product.autoStatus],
  ["/export", FULLSTORY_PAGES.product.printExport],
  ["/", FULLSTORY_PAGES.marketing.landing],
];

const BLOCKED_ROUTE_PREFIXES = [
  "/admin",
  "/admin-panel",
  "/share",
  "/school/",
  "/purchase-order",
  "/spotify-callback",
  "/canva-callback",
  "/canva-return",
  "/google-drive-callback",
  "/gmail-callback",
  "/dev/",
  "/debug",
  "/auth/logout",
  "/contact",
  "/quote",
  "/automation-preview",
];

function normalizePathname(pathname) {
  const rawPathname = typeof pathname === "string" ? pathname : "";
  const withoutQuery = rawPathname.split("?")[0].split("#")[0] || "/";
  if (withoutQuery.length > 1 && withoutQuery.endsWith("/")) {
    return withoutQuery.slice(0, -1);
  }
  return withoutQuery;
}

function isBlockedPathname(pathname) {
  return BLOCKED_ROUTE_PREFIXES.some((prefix) => (
    prefix.endsWith("/")
      ? pathname.startsWith(prefix)
      : pathname === prefix || pathname.startsWith(`${prefix}/`)
  ));
}

function isSingleSegmentProductPath(pathname) {
  return /^\/[^/]+$/.test(pathname)
    && pathname !== "/"
    && !pathname.includes(".");
}

export function resolveFullstoryPageName(pathname) {
  const normalizedPathname = normalizePathname(pathname);

  if (isBlockedPathname(normalizedPathname)) {
    return null;
  }

  if (normalizedPathname.startsWith("/content-manager/")) {
    return FULLSTORY_PAGES.product.contentManager;
  }

  if (normalizedPathname.startsWith("/calendar/")) {
    return FULLSTORY_PAGES.product.presentCalendar;
  }

  if (normalizedPathname.startsWith("/blog")) {
    return FULLSTORY_PAGES.marketing.blog;
  }

  if (normalizedPathname.startsWith("/compare/")) {
    return FULLSTORY_PAGES.marketing.comparison;
  }

  if (normalizedPathname.startsWith("/demo/")) {
    return FULLSTORY_PAGES.marketing.demoScenario;
  }

  if (normalizedPathname.startsWith("/r/")) {
    return FULLSTORY_PAGES.marketing.referralLanding;
  }

  if (
    normalizedPathname.startsWith("/classroom-")
    || normalizedPathname.startsWith("/countdown-")
    || normalizedPathname.startsWith("/random-")
  ) {
    return FULLSTORY_PAGES.marketing.classroomTool;
  }

  const staticMatch = STATIC_PAGE_ROUTES.find(([routePath]) => routePath === normalizedPathname);
  if (staticMatch) {
    return staticMatch[1];
  }

  if (isSingleSegmentProductPath(normalizedPathname)) {
    return FULLSTORY_PAGES.product.present;
  }

  return null;
}
